import { useContext, useState, createContext, useReducer, useEffect } from 'react'
import userReducer from './UserReducer'
import Cookies from 'js-cookie';
import {obtenerDominio} from "../../utils/utilsFunct";
import { validateToken } from './UserActions';

const keyToken = process.env.REACT_APP_SESSION_KEY || 'alleanza_jwtoken'

const UserContext = createContext()

const initialState = {
  isAuthenticated: false,
  userData: {},
  users: [],
  user: {},
  session: null,
  activeSessions: false,
  industries: [],
  uses: [],
  tempPass: null,
  newUser: {},
  token: '',
  authenticatedUser: {},
  message: '',
  msgType: '',
  link: false,
  url: '',
  showAlert: false,
  loading: true, // Start with loading true to prevent race conditions
}

const getToken = () => {
  try {
    const tokenString = localStorage.getItem(keyToken)
    if (!tokenString) return null
    
    const userToken = JSON.parse(tokenString)
    if(!userToken) { return null }
    
    if (Object.prototype.hasOwnProperty.call(userToken, 'expiry')) {
        if(new Date().getTime() > userToken.expiry) {
            localStorage.removeItem(keyToken)
            return null
        }
    }
    return userToken.value
  } catch (error) {
    localStorage.removeItem(keyToken)
    return null
  }
}

const getInitialState = () => {
  try {
    const tokenString = localStorage.getItem(keyToken)
    if (!tokenString) return initialState
    
    const userToken = JSON.parse(tokenString)
    if (!userToken) return initialState
    
    // Verificar si el token ha expirado
    if (Object.prototype.hasOwnProperty.call(userToken, 'expiry')) {
      if (new Date().getTime() > userToken.expiry) {
        localStorage.removeItem(keyToken)
        return initialState
      }
    }
    
    return {
      ...initialState,
      authenticatedUser: { ...userToken, pendingPlan: false },
      isAuthenticated: true,
      token: userToken.value,
      loading: true // Keep loading true until server validation completes
    }
  } catch (error) {
    localStorage.removeItem(keyToken)
    return initialState
  }
}

export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, getInitialState())
  const [token, setToken] = useState(getToken())
  
  // Efecto para sincronizar el estado con localStorage al montar el componente
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const tokenString = localStorage.getItem(keyToken)
        if (!tokenString) {
          dispatch({ type: 'LOGOUT_USER', payload: {} })
          return
        }
        
        const userToken = JSON.parse(tokenString)
        if (!userToken || !userToken.value) {
          localStorage.removeItem(keyToken)
          dispatch({ type: 'LOGOUT_USER', payload: {} })
          return
        }
        
        // Verificar si el token ha expirado localmente
        if (Object.prototype.hasOwnProperty.call(userToken, 'expiry')) {
          if (new Date().getTime() > userToken.expiry) {
            localStorage.removeItem(keyToken)
            dispatch({ type: 'LOGOUT_USER', payload: {} })
            return
          }
        }
        
        // Validar token con el servidor
        try {
          const userData = await validateToken({ token: userToken.value, type: 'token' })
          if (userData && userData.permissions) {
            // Token válido, actualizar datos del usuario
            const updatedUserToken = {
              ...userToken,
              permissions: userData.permissions,
              fullName: userData.fullName || userToken.fullName,
              pendingPlan: false,
            }
            localStorage.setItem(keyToken, JSON.stringify(updatedUserToken))
            dispatch({ type: 'AUTH_USER', payload: updatedUserToken })
          } else {
            // Token inválido
            localStorage.removeItem(keyToken)
            dispatch({ type: 'LOGOUT_USER', payload: {} })
          }
        } catch (validationError) {
          console.warn('Token validation failed:', validationError)
          // Si falla la validación, mantener el token local pero marcar como no autenticado
          // Esto permite que la app funcione offline pero requerirá reautenticación
          localStorage.removeItem(keyToken)
          dispatch({ type: 'LOGOUT_USER', payload: {} })
        }
        
      } catch (error) {
        console.warn('Error initializing auth:', error)
        localStorage.removeItem(keyToken)
        dispatch({ type: 'LOGOUT_USER', payload: {} })
      }
    }
    
    // Ejecutar validación si hay un token pero aún no se ha validado con el servidor
    if (state.isAuthenticated && state.loading) {
      initializeAuth()
    } else if (!state.isAuthenticated) {
      dispatch({ type: 'LOADING_USER' })
      initializeAuth()
    }
  }, []) // Solo ejecutar una vez al montar
  
  // Configurar cookies solo si hay un token válido
  useEffect(() => {
    const userToken = state.authenticatedUser
    if (userToken && userToken.value) {
      try {
        Cookies.set('keyToken', `${userToken.value}`, { domain: `.${obtenerDominio()}`});
        Cookies.set('appName', 'alleanza', { domain: `.${obtenerDominio()}`});
      } catch (error) {
        console.warn('Error setting cookies:', error)
      }
    }
  }, [state.authenticatedUser])

  const authenticateUser = (user) => {
    dispatch({ type: 'AUTH_USER', payload: user })
  }

  const logOutUser = (user) => {
    // Limpiar localStorage y cookies
    localStorage.removeItem(keyToken)
    try {
      Cookies.remove('keyToken', { domain: `.${obtenerDominio()}` })
      Cookies.remove('appName', { domain: `.${obtenerDominio()}` })
    } catch (error) {
      console.warn('Error removing cookies:', error)
    }
    setToken(null)
    dispatch({ type: 'LOGOUT_USER', payload: user })
  }

  const setUsers = (users) => {
    dispatch({ type: 'GET_USERS', payload: users })
  }

  const setUser = (user) => {
    dispatch({ type: 'GET_USER', payload: user })
  }

  const setSession = (session) => {
    dispatch({ type: 'SET_SESSION', payload: session })
  }

  const setActiveSessions = (session) => {
    dispatch({ type: 'SET_ACTIVE_SESSION', payload: session })
  }

  const setIndustries = (industries) => {
    dispatch({ type: 'SET_INDUSTRIES', payload: industries  })
  }

  const setUses = (uses) => {
    dispatch({ type: 'SET_USES', payload: uses })
  }

  const setAlert = (alert) => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    })
    dispatch({ type: 'SET_ALERT', payload: alert })
  }

  const disableAlert = () => {
    dispatch({ type: 'DISABLE_ALERT', payload: false })
  }

  return (
    <UserContext.Provider
      value={{
        ...state,
        token,
        setAlert,
        disableAlert,
        setToken,
        authenticateUser,
        logOutUser,
        setUsers,
        setUser,
        setSession,
        setActiveSessions,
        setIndustries,
        setUses,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export const useUserContext = () => {
  return useContext(UserContext)
}
